import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Receipt, CheckCircle2, CreditCard, ShieldCheck } from "lucide-react";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoicePublic() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payLoading, setPayLoading] = useState(false);

  const sessionId = params.get("session_id");

  const load = async () => {
    try { const { data } = await axios.get(`${API}/public/invoices/${token}`); setInv(data); }
    catch { setInv({ error: true }); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line

  // If returning from Stripe with session_id, poll payment status
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Payment confirmed 🎉");
          load();
          return;
        }
      } catch {}
      if (attempts < 15 && !cancelled) setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line

  const pay = async () => {
    setPayLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/invoices/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stripe error");
      setPayLoading(false);
    }
  };

  const openPay = (opt) => {
    if (opt.method === "stripe_checkout") { pay(); return; }
    window.open(opt.url, "_blank", "noopener");
    setPendingMethod(opt.method);
  };
  const reportSent = async (method) => {
    try {
      await axios.post(`${API}/public/invoices/${token}/report-payment`, { method });
      toast.success("Thanks — we'll verify shortly");
      setShowThanks(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const [pendingMethod, setPendingMethod] = useState(null);
  const [showThanks, setShowThanks] = useState(false);

  if (loading) return <Center>Loading…</Center>;
  if (!inv || inv.error) return <Center><Receipt className="w-10 h-10 text-zinc-300 mb-3"/><div className="font-heading text-2xl font-bold">Invoice not found</div></Center>;

  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-4">
      <Toaster richColors position="top-right"/>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-zinc-900 text-white px-3 py-1.5 rounded-lg">
            <Receipt className="w-4 h-4 text-[#FF5A00]"/>
            <span className="font-heading font-bold">{inv.business?.name || "LeadForge"}</span>
          </div>
          {(inv.business?.address || inv.business?.phone || inv.business?.email) && (
            <div className="mt-2 text-xs text-zinc-600" data-testid="invoice-public-biz-info">
              {[inv.business.address, inv.business.phone, inv.business.email].filter(Boolean).join(" · ")}
            </div>
          )}
          <div className="mt-3 text-xs font-mono-accent uppercase tracking-widest text-zinc-500">Invoice {inv.invoice_number}</div>
        </div>
        {(showThanks || inv.status === "paid") && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-5 text-center" data-testid="invoice-thanks-card">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2"/>
            <div className="font-heading text-xl font-bold">
              {inv.status === "paid" ? "Thank you! Your payment has been received." : "Thank you! Your payment has been reported."}
            </div>
            <div className="text-sm text-zinc-600 mt-2">
              — {inv.business?.name}{inv.business?.phone ? ` · ${inv.business.phone}` : ""}
            </div>
          </div>
        )}
        {inv.status !== "paid" && !showThanks && (inv.pay_options || []).length > 0 && (
          <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-5" data-testid="pay-options-card">
            <div className="text-sm font-semibold mb-3">Pay this invoice</div>
            <div className="grid grid-cols-1 gap-2">
              {(inv.pay_options || []).map((opt) => (
                <button key={opt.method} onClick={() => openPay(opt)}
                        disabled={payLoading}
                        className="w-full bg-zinc-900 text-white rounded-lg py-3 text-sm font-semibold"
                        data-testid={`pay-btn-${opt.method}`}>{opt.label}</button>
              ))}
            </div>
            {pendingMethod && (
              <button onClick={() => reportSent(pendingMethod)}
                      className="w-full mt-3 border border-emerald-300 bg-emerald-50 text-emerald-900 rounded-lg py-3 text-sm font-semibold"
                      data-testid="ive-sent-payment-btn">
                I've sent my payment
              </button>
            )}
          </div>
        )}
        <div className="hidden">
          <div className="mt-3 text-xs font-mono-accent uppercase tracking-widest text-zinc-500">Invoice {inv.invoice_number}</div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
          {/* Styled document — same HTML used for PDF/email/print */}
          <iframe
            title="invoice"
            src={`${API}/public/invoices/${token}/html`}
            className="w-full bg-white"
            style={{ minHeight: "1100px", border: "none", display: "block" }}
            data-testid="invoice-doc-iframe"
          />

          {inv.status === "paid" && (
            <div className="mx-8 mt-2 mb-6 flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <CheckCircle2 className="w-6 h-6 text-emerald-600"/>
              <div>
                <div className="font-medium text-emerald-900">Payment received</div>
                <div className="text-xs text-emerald-700">Paid on {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : ""}</div>
              </div>
            </div>
          )}

          {inv.status !== "paid" && (
            <div className="bg-zinc-50 border-t border-zinc-200 p-6">
              <button onClick={pay} disabled={payLoading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 rounded-lg flex items-center justify-center gap-2 btn-lift disabled:opacity-50" data-testid="public-pay-btn">
                <CreditCard className="w-5 h-5"/> {payLoading ? "Redirecting to Stripe…" : `Pay $${inv.total.toFixed(2)}`}
              </button>
              <div className="text-center mt-3 text-xs text-zinc-500 flex items-center justify-center gap-1"><ShieldCheck className="w-3 h-3"/> Powered by Stripe · Test card 4242 4242 4242 4242</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
function Row({ label, value }) { return <div className="flex justify-between"><span className="text-zinc-600">{label}</span><span className="font-mono-accent text-zinc-900">{value}</span></div>; }
function Center({ children }) { return <div className="min-h-screen flex items-center justify-center flex-col bg-zinc-50 p-8">{children}</div>; }
